import { z } from 'zod'

export const MAX_FEE_RUPIAH = 999_999_999
export const MAX_FEE_TIERS = 10
/** BE limit for `percentage` and a tier's PERCENTAGE `value` (0–100 inclusive). */
export const MAX_FEE_PERCENT = 100

export const feeMethodSchema = z.enum(['flat', 'percentage', 'tier', 'custom'])

export const feeTierSchema = z.object({
  start: z.number().int().nonnegative(),
  /** `null` only on the last tier: "dan seterusnya". */
  end: z.number().int().nonnegative().nullable(),
  type: z.enum(['amount', 'percent']),
  value: z.number().nonnegative(),
})

/** Global Jastip Fee config (FE model). Fee is per product unit, then multiplied by quantity. */
export const jastipFeeConfigSchema = z.discriminatedUnion('method', [
  /** `amount` = starting fee per product before the seller fills it in (default Rp0). */
  z.object({ method: z.literal('custom'), amount: z.number().int().nonnegative() }),
  // WEBLP-3164 AC5: fees are required and never negative (Rp0 allowed).
  z.object({ method: z.literal('flat'), amount: z.number().int().nonnegative() }),
  z.object({
    method: z.literal('percentage'),
    percent: z.number().nonnegative().max(MAX_FEE_PERCENT),
    minimum: z.number().int().nonnegative(),
  }),
  z.object({
    method: z.literal('tier'),
    tiers: z.array(feeTierSchema).min(1).max(MAX_FEE_TIERS),
  }),
])

/** New seller default, also returned by the BE when nothing is saved (WEBLP-3210). */
export const DEFAULT_JASTIP_FEE_CONFIG: JastipFeeConfig = { method: 'custom', amount: 0 }

/*
 * Wire format of GET/PUT /v1/algo-pos-be/liotitip/configuration (WEBLP-3209/3210).
 * GET does not revalidate stored data, so every field is optional here and
 * `fromWire` decides whether the shape is usable.
 */
const wireNumber = z.number().nullish()

export const jastipFeeTierWireSchema = z.object({
  min_price: wireNumber,
  max_price: wireNumber,
  type: z.string().nullish(),
  value: wireNumber,
})

export const jastipFeeWireSchema = z.object({
  method: z.string().nullish(),
  amount: wireNumber,
  percentage: wireNumber,
  minimum_fee: wireNumber,
  tiers: z.array(jastipFeeTierWireSchema).nullish(),
})

export const jastipFeeResponseSchema = z.object({
  jastip_fee: z.unknown().nullish(),
})

export type FeeMethod = z.infer<typeof feeMethodSchema>
export type FeeTier = z.infer<typeof feeTierSchema>
export type JastipFeeConfig = z.infer<typeof jastipFeeConfigSchema>
export type JastipFeeWire = z.infer<typeof jastipFeeWireSchema>
export type JastipFeeTierWire = z.infer<typeof jastipFeeTierWireSchema>
